/**
 * Centralized logging utility
 * Automatically disables logging in production to improve performance and security
 * 
 * Usage:
 *   import { logger } from '@/utils/logger';
 *   logger.log('Info message');
 *   logger.warn('Warning message');
 *   logger.error('Error message'); // Always logged, even in production
 *   logger.debug('Debug message'); // Only in development
 */

const IS_DEV = typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV;
const IS_PROD = typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.PROD;

/**
 * Production-safe logger
 * - log/warn/debug: Only in development
 * - error: Always logged (important for production debugging)
 */
export const logger = {
  /**
   * Log informational messages (development only)
   */
  log: (...args) => {
    if (IS_DEV) {
      console.log(...args);
    }
  },

  /**
   * Log warning messages (development only)
   */
  warn: (...args) => {
    if (IS_DEV) {
      console.warn(...args);
    }
  },

  /**
   * Log error messages (always logged, even in production)
   * Errors are important for debugging production issues
   */
  error: (...args) => {
    // Always log errors, but in production we can send to error tracking service
    if (IS_PROD) {
      // In production, you might want to send to Sentry or similar
      // For now, we'll still log but could enhance this later
      console.error('[PROD]', ...args);
    } else {
      console.error(...args);
    }
  },

  /**
   * Log debug messages (development only)
   */
  debug: (...args) => {
    if (IS_DEV) {
      console.debug(...args);
    }
  },

  /**
   * Log with a specific level
   */
  info: (...args) => {
    if (IS_DEV) {
      console.info(...args);
    }
  },

  /**
   * Group related logs (development only)
   */
  group: (...args) => {
    if (IS_DEV) {
      console.group(...args);
    }
  },

  /**
   * End a log group (development only)
   */
  groupEnd: () => {
    if (IS_DEV) {
      console.groupEnd();
    }
  },

  /**
   * Group collapsed logs (development only)
   */
  groupCollapsed: (...args) => {
    if (IS_DEV) {
      console.groupCollapsed(...args);
    }
  },

  /**
   * Table logging (development only)
   */
  table: (...args) => {
    if (IS_DEV) {
      console.table(...args);
    }
  },
};

/**
 * Check if logging is enabled
 */
export const isLoggingEnabled = () => IS_DEV;

/**
 * Legacy compatibility - for files that use devLog pattern
 */
export const devLog = logger.log;
export const devWarn = logger.warn;
export const devError = logger.error;

export default logger;

